/**
 * GTFS Static Data service - R2 only
 * Fetches pre-processed GTFS data from Cloudflare R2
 */

import * as Sentry from "@sentry/browser";
import { getVehicleType } from '../utils/vehicleTypes';
import type { RoutesData, TripsData, ShapesData } from '../types/gtfs-rt';

const R2_PUBLIC_URL = import.meta.env.VITE_R2_PUBLIC_URL;

if (!R2_PUBLIC_URL) {
  console.error('[GTFS] VITE_R2_PUBLIC_URL not configured');
}

// In-memory cache for GTFS static data
const dataCache = new Map<string, {
  routes?: RoutesData;
  trips?: TripsData;
  shapes?: ShapesData;
  timestamp: number;
}>();

const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours


/**
 * Fetch data from Cloudflare R2
 */
async function fetchFromR2<T>(operator: string, dataType: 'routes' | 'trips' | 'shapes'): Promise<T> {
  const url = `${R2_PUBLIC_URL}/gtfs/${operator}/${dataType}.json`;
  
  try {
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: T = await response.json();
    console.log(`[GTFS R2] Loaded ${dataType} for ${operator}`);
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error(`[GTFS R2] Failed to fetch ${dataType} for ${operator}:`, message);
    
    // Capture errors with Sentry
    Sentry.captureException(error, {
      tags: {
        service: 'gtfs-static',
        source: 'R2'
      },
      contexts: {
        gtfs: {
          operator,
          dataType
        }
      }
    });
    
    throw error;
  }
}

/**
 * Get cached data for an operator, checking TTL
 */
function getCachedData(operator: string): typeof dataCache extends Map<string, infer V> ? V | undefined : never {
  const entry = dataCache.get(operator);
  if (!entry) return undefined;

  // Check if cache is still valid
  const age = Date.now() - entry.timestamp;
  if (age > CACHE_TTL) {
    console.log(`[GTFS Cache] Cache expired for ${operator}`);
    dataCache.delete(operator);
    return undefined;
  }

  return entry;
}

/**
 * Set cached data for an operator
 */
function setCachedData(
  operator: string,
  data: Partial<Omit<typeof dataCache extends Map<string, infer V> ? V : never, 'timestamp'>>
): void {
  const entry = dataCache.get(operator) || { timestamp: Date.now() };
  dataCache.set(operator, { ...entry, ...data, timestamp: Date.now() });
}


/**
 * Enrich routes data with vehicle type information at runtime
 */
function enrichRoutesWithVehicleTypes(routes: RoutesData): RoutesData {
  const enrichedRoutes = { ...routes };
  
  Object.keys(enrichedRoutes.routes).forEach((routeId) => {
    const route = enrichedRoutes.routes[routeId];
    const vehicleType = getVehicleType(route.type);
    
    // Add icon and enhanced color information
    route.icon = vehicleType.icon;
    route.displayName = vehicleType.displayName;
    route.vehicleDescription = vehicleType.description;
  });

  return enrichedRoutes;
}

/**
 * Get estimated storage usage in bytes
 */
export async function getStorageUsage(): Promise<{ usage: number; quota: number }> {
  if (navigator.storage && navigator.storage.estimate) {
    const estimate = await navigator.storage.estimate();
    return {
      usage: estimate.usage || 0,
      quota: estimate.quota || 0
    };
  }
  return { usage: 0, quota: 0 };
}

/**
 * Get routes data from R2 (cached)
 */
export async function getRoutesData(operator: string): Promise<RoutesData> {
  const cached = getCachedData(operator);
  if (cached?.routes) {
    console.log(`[GTFS] Using cached routes for ${operator}`);
    return cached.routes;
  }

  const routes = await fetchFromR2<RoutesData>(operator, 'routes');
  const enriched = enrichRoutesWithVehicleTypes(routes);
  setCachedData(operator, { routes: enriched });
  return enriched;
}

/**
 * Get trips data from R2 (cached)
 */
export async function getTripsData(operator: string): Promise<TripsData> {
  const cached = getCachedData(operator);
  if (cached?.trips) {
    console.log(`[GTFS] Using cached trips for ${operator}`);
    return cached.trips;
  }

  const trips = await fetchFromR2<TripsData>(operator, 'trips');
  setCachedData(operator, { trips });
  return trips;
}

/**
 * Get shapes data from R2 (cached)
 */
export async function getShapesData(operator: string): Promise<ShapesData> {
  const cached = getCachedData(operator);
  if (cached?.shapes) {
    console.log(`[GTFS] Using cached shapes for ${operator}`);
    return cached.shapes;
  }

  const shapes = await fetchFromR2<ShapesData>(operator, 'shapes');
  setCachedData(operator, { shapes });
  return shapes;
}

/**
 * Get all GTFS static data from R2 (cached)
 */
export async function getAllGTFSData(operator: string): Promise<{
  routes: RoutesData;
  trips: TripsData;
  shapes: ShapesData;
}> {
  const cached = getCachedData(operator);
  
  // Check if we have all data cached
  if (cached?.routes && cached?.trips && cached?.shapes) {
    console.log(`[GTFS] Using all cached data for ${operator}`);
    return {
      routes: cached.routes,
      trips: cached.trips,
      shapes: cached.shapes
    };
  }

  const [routes, trips, shapes] = await Promise.all([
    cached?.routes ? Promise.resolve(cached.routes) : fetchFromR2<RoutesData>(operator, 'routes'),
    cached?.trips ? Promise.resolve(cached.trips) : fetchFromR2<TripsData>(operator, 'trips'),
    cached?.shapes ? Promise.resolve(cached.shapes) : fetchFromR2<ShapesData>(operator, 'shapes')
  ]);

  const enrichedRoutes = cached?.routes ? routes : enrichRoutesWithVehicleTypes(routes);
  setCachedData(operator, { routes: enrichedRoutes, trips, shapes });

  return {
    routes: enrichedRoutes,
    trips,
    shapes
  };
}
